import React, { useState, useEffect } from 'react';
import { BarChart3, Download, RefreshCw, AlertCircle, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import { ExportButtons } from './ExportButtons';
import { csvCell } from '../utils/csvSafe';

// ─── Types ────────────────────────────────────────────────────────────────────
// Field names match exactly what the Lambda SELECT returns
interface MassBalanceRow {
  facility_code: string;
  facility_name: string;
  material_code: string;
  inbound: string;   // Lambda casts these to VARCHAR
  outbound: string;
  variance: string;
  status: string;
  notes: string;
}

interface Filters {
  year: string;
  month: string;
  facility: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS = [
  { value: '1',  label: 'January'   },
  { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     },
  { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       },
  { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      },
  { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' },
  { value: '10', label: 'October'   },
  { value: '11', label: 'November'  },
  { value: '12', label: 'December'  },
];

const STATUS_STYLES: Record<string, string> = {
  'Balanced':                  'bg-green-100  text-green-800',
  'Minor (<5%)':               'bg-yellow-100 text-yellow-800',
  'Check':                     'bg-red-100    text-red-800',
  'Output Only (Recovered)':   'bg-blue-100   text-blue-800',
  'Input Only (Transformed)':  'bg-purple-100 text-purple-800',
};

const now = new Date();
const CURRENT_YEAR  = now.getFullYear();
const CURRENT_MONTH = String(now.getMonth() + 1);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toNum(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function fmtTons(s: string): string {
  const n = toNum(s);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * BUG FIX #3 — Variance % was not returned by the Lambda.
 * Calculate it client-side from the inbound and variance values.
 */
function calcVariancePct(inbound: string, variance: string): string {
  const ib = toNum(inbound);
  const v  = toNum(variance);
  if (ib === 0) return '—';
  return (v / ib * 100).toFixed(1) + '%';
}

// ─── Component ────────────────────────────────────────────────────────────────
export function MassBalance() {
  const { getToken } = useAuth();

  const [filters, setFilters] = useState<Filters>({
    year:     String(CURRENT_YEAR),
    month:    CURRENT_MONTH,
    facility: '',
  });

  const [data,      setData]      = useState<MassBalanceRow[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [facilities, setFacilities] = useState<{ facility_code: string; facility_name: string }[]>([]);

  const API_URL = process.env.REACT_APP_API_URL ?? '';

  // ── Fetch ──────────────────────────────────────────────────────────────────
useEffect(() => {
  const loadFacilities = async () => {
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/reports/generate`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ report_id: 'lookup.facilities', params: {} }),
      });
      const result = await res.json();
      setFacilities(result.data ?? []);
    } catch (e) {
      console.error('Failed to load facilities:', e);
    }
  };
  loadFacilities();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const fetchReport = async () => {
    if (!filters.year || !filters.month) return;
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: Record<string, string> = {
        year:  filters.year,
        month: filters.month,
      };
      if (filters.facility) params.facility = filters.facility;

      const res = await fetch(`${API_URL}/reports/generate`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ report_id: '2.1', params }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      const result = await res.json();
      setData(result.data ?? []);
      setHasLoaded(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  // ── CSV Export ─────────────────────────────────────────────────────────────
  /**
   * BUG FIX #1 — Year and Month are NOT in the Lambda response rows.
   *              Inject them from the filter state.
   * BUG FIX #2 — Use the correct Lambda field names:
   *              facility_name, material_code, inbound, outbound, variance.
   * BUG FIX #3 — Variance % must be computed here; the Lambda does not return it.
   */
  const exportCSV = () => {
    const monthLabel = MONTHS.find(m => m.value === filters.month)?.label ?? filters.month;

    const headers = [
      'Year', 'Month', 'Facility', 'Material',
      'Inbound (tons)', 'Outbound (tons)', 'Variance (tons)', 'Variance (%)',
      'Status', 'Notes',
    ];

    const rows = data.map(row => [
      filters.year,                                      // BUG FIX #1
      monthLabel,                                        // BUG FIX #1
      row.facility_name,                                 // BUG FIX #2
      row.material_code,                                 // BUG FIX #2
      row.inbound,                                       // BUG FIX #2
      row.outbound,                                      // BUG FIX #2
      row.variance,                                      // BUG FIX #2
      calcVariancePct(row.inbound, row.variance),        // BUG FIX #3
      row.status,
      row.notes ?? '',
    ]);

    const csv = [headers, ...rows]
      .map(r => r.map(c => csvCell(c)).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `mass-balance-${filters.year}-${filters.month.padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Derived filter options from loaded data ────────────────────────────────
  // facilities comes from lookup.facilities (loaded on mount)

  // ── Summary totals ─────────────────────────────────────────────────────────
  const totalInbound  = data.reduce((s, r) => s + toNum(r.inbound),  0);
  const totalOutbound = data.reduce((s, r) => s + toNum(r.outbound), 0);
  const totalVariance = totalInbound - totalOutbound;
  const checkCount    = data.filter(r => r.status === 'Check').length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mass Balance Report</h1>
              <p className="text-sm text-gray-500">Material flow reconciliation by facility</p>
            </div>
          </div>

{hasLoaded && data.length > 0 && (
  <div className="flex items-center space-x-2">
    <button
      onClick={exportCSV}
      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
    >
      <Download className="w-4 h-4" />
      <span>Export CSV</span>
    </button>
    <ExportButtons
      reportTitle="Mass Balance Report"
      subtitle={`${MONTHS.find(m => m.value === filters.month)?.label ?? ''} ${filters.year}`}
      getExportData={() => {
        const monthLabel = MONTHS.find(m => m.value === filters.month)?.label ?? filters.month;
        return {
          columns: ['Year', 'Month', 'Facility', 'Material', 'Inbound (tons)', 'Outbound (tons)', 'Variance (tons)', 'Variance (%)', 'Status', 'Notes'],
          data: data.map(row => ({
            'Year': filters.year,
            'Month': monthLabel,
            'Facility': row.facility_name,
            'Material': row.material_code,
            'Inbound (tons)': row.inbound,
            'Outbound (tons)': row.outbound,
            'Variance (tons)': row.variance,
            'Variance (%)': calcVariancePct(row.inbound, row.variance),
            'Status': row.status,
            'Notes': row.notes ?? '',
          })),
        };
      }}
    />
  </div>
)}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* ── Filter Panel ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Year */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Year <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={filters.year}
                min="2020"
                max={CURRENT_YEAR}
                onChange={e => setFilters(f => ({ ...f, year: e.target.value, facility: '' }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Month */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Month <span className="text-red-500">*</span>
              </label>
              <select
                value={filters.month}
                onChange={e => setFilters(f => ({ ...f, month: e.target.value, facility: '' }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Facility — populated after first load */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Facility</label>
              <select
                value={filters.facility}
                onChange={e => setFilters(f => ({ ...f, facility: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Facilities</option>
                {facilities.map(fac => (
                  <option key={fac.facility_code} value={fac.facility_code}>
                    {fac.facility_name || fac.facility_code}
                  </option>
                ))}
              </select>
            </div>

          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={fetchReport}
              disabled={loading || !filters.year || !filters.month}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {/* ── Error ──────────────────────────────────────────────────────────── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* ── Summary Cards ──────────────────────────────────────────────────── */}
        {hasLoaded && data.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                label: 'Total Inbound',
                value: totalInbound.toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' tons',
                color: 'text-blue-600',
              },
              {
                label: 'Total Outbound',
                value: totalOutbound.toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' tons',
                color: 'text-green-600',
              },
              {
                label: 'Net Variance',
                value: totalVariance.toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' tons',
                color: totalVariance < -0.5 ? 'text-red-600' : 'text-gray-800',
              },
              {
                label: 'Items to Check',
                value: String(checkCount),
                color: checkCount > 0 ? 'text-red-600' : 'text-green-600',
              },
            ].map(card => (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Data Table ─────────────────────────────────────────────────────── */}
        {hasLoaded && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {data.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-25" />
                <p className="text-sm font-medium">No data found for the selected filters.</p>
                <p className="text-xs mt-1 text-gray-400">Try adjusting Year, Month, or removing filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {[
                        'Facility', 'Material',
                        'Inbound (tons)', 'Outbound (tons)', 'Variance (tons)', 'Variance (%)',
                        'Status', 'Notes',
                      ].map(col => (
                        <th
                          key={col}
                          className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((row, i) => {
                      const variancePct = calcVariancePct(row.inbound, row.variance);
                      const varianceNum = toNum(row.variance);
                      return (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                            {row.facility_name}
                          </td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                            {row.material_code}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800 tabular-nums">
                            {fmtTons(row.inbound)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800 tabular-nums">
                            {fmtTons(row.outbound)}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium tabular-nums ${varianceNum < -0.5 ? 'text-red-600' : 'text-gray-800'}`}>
                            {fmtTons(row.variance)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums">
                            {variancePct}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-700'}`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-xs italic max-w-xs truncate">
                            {row.notes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Initial Empty State ────────────────────────────────────────────── */}
        {!hasLoaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select filters and click Run Report</p>
            <p className="text-sm text-gray-400 mt-1">Year and Month are required</p>
          </div>
        )}

      </main>
    </div>
  );
}
